/* eslint-disable prettier/prettier */
/* eslint-disable arrow-parens */
import { useState, useCallback, useEffect, useContext } from "react";
import { AgGridColumn, AgGridReact } from "ag-grid-react";
import { GridReadyEvent } from "ag-grid-community";
import { useMediaQuery } from "@material-ui/core";
import { trackPromise } from "react-promise-tracker";

import "ag-grid-community/dist/styles/ag-grid.css";
import "ag-grid-community/dist/styles/ag-theme-alpine.css";

import { UIContext } from "../../../../contexts";
import { ADMIN_DASHBOARD_HEADER } from "../../../../constants/caption/dashboardHeader";
import CustomPagination from "../../../shared/customPagination/CustomPagination1";
import { CustomFilter, CustomFloatingFilter } from "../../../shared";
import { PaginationQuery, PaginationTypes } from "../../../../@types/pagination.types";
import getLimit from "../../../../utils/pagination/getLimit";
import useRouteParam from "../../../../hooks/useRouteParam";
import { dateTimeZoneConverter } from "../../../../utils/commonFunctions/date";
import { api } from "../../../../utils/api";
import ReportActionsCell from "./ReportActionsCell";

import "./scanReports.css";

/**
 * The only page the USER role can reach: the scan list for the store the account is
 * assigned to, with Download / Print for each scan's report.
 *
 * The server pins group_number and store_number from the token, so the params sent here
 * only narrow the query - they cannot widen it to another store.
 */
const ScanReports = (): JSX.Element => {
	const { setDashboardHeader } = useContext(UIContext);
	const group_number = useRouteParam("gn");
	const store_number = useRouteParam("sn");
	const [limit, setLimit] = useState<number | null>(null);
	const [scanList, setScanList] = useState<any[]>([]);
	const [pagination, setPagination] = useState<PaginationTypes>({
		currentPage: 1,
		pageCount: undefined
	});
	const { currentPage, pageCount } = pagination;

	// Only fields that live on the scan document itself are filterable. Customer name
	// comes from a populated virtual, so the server cannot match a regex against it.
	const [filterSchema, setFilterSchema] = useState<any>({
		customer_id: { value: null, type: null },
		customer_scan_id: { value: null, type: null },
		device_id: { value: null, type: null },
		phone: { value: null, type: null }
	});

	const xl = useMediaQuery("(min-width:1600px)");
	const lg = useMediaQuery("(min-width:1200px)");
	const md = useMediaQuery("(min-width:800px)");
	const sm = useMediaQuery("(min-width:600px)");
	const xs = useMediaQuery("(min-width:400px)");
	const _limit = getLimit(xl, lg, md, sm, xs);

	const handleAgGridReady = (params: GridReadyEvent) => {
		params.api.sizeColumnsToFit();
	};

	const handleFirstDataRendered = (params: any) => {
		params.api.sizeColumnsToFit();
	};

	const handleDateCellRenderer = ({ value }: { value: string }) => dateTimeZoneConverter(value);

	const getPaginatedScanList = useCallback(async () => {
		if (typeof limit !== "number" || typeof pagination.currentPage !== "number") {
			return;
		}

		const paginationQuery: PaginationQuery = {
			limit: limit,
			page: pagination.currentPage
		};

		const filterQuery: any = { ...paginationQuery, group_number, store_number };
		Object.keys(filterSchema).forEach((key) => {
			if (filterSchema[key].value !== null) {
				filterQuery[key] = { $regex: filterSchema[key].value, $options: "i" };
			}
		});

		const response = await trackPromise(api.customer.getPaginatedScanList(filterQuery));
		if (response) {
			setScanList(Object.assign([], response.scans));
			setPagination(
				Object.assign({}, pagination, {
					pageCount: response.totalPages
				})
			);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [limit, pagination.currentPage, group_number, store_number, filterSchema]);

	const handlePageChange = useCallback(
		(event: React.ChangeEvent<unknown>, page: number) => {
			setPagination(
				Object.assign({}, pagination, {
					currentPage: page
				})
			);
		},
		[pagination]
	);

	useEffect(() => {
		if (_limit >= 0) {
			setLimit(_limit);
			getPaginatedScanList();
		}
		setDashboardHeader(ADMIN_DASHBOARD_HEADER.scan_reports);
	}, [_limit, getPaginatedScanList, setDashboardHeader]);

	return (
		<div className="scan-reports-page-container">
			<div className="ag-theme-alpine" style={{ height: "400px", width: "100%" }}>
				<AgGridReact
					rowData={scanList}
					onGridReady={handleAgGridReady}
					onFirstDataRendered={handleFirstDataRendered}
					rowSelection={"Single"}
					animateRows
					rowHeight={52}
					suppressColumnVirtualisation={true}
					defaultColDef={{
						filter: "agTextColumnFilter",
						floatingFilter: true,
						resizable: true
					}}
					enableCellChangeFlash={true}
					frameworkComponents={{
						handleDateCellRenderer: handleDateCellRenderer,
						reportActionsCell: ReportActionsCell,
						customFloatingFilter: CustomFloatingFilter,
						customFilter: CustomFilter,
						schema: filterSchema,
						setPayloadSchema: setFilterSchema
					}}
				>
					<AgGridColumn
						field="customer_id"
						headerName="Customer Id"
						floatingFilterComponent="customFloatingFilter"
						filter="customFilter"
						cellStyle={() => ({ textAlign: "left" })}
					></AgGridColumn>
					<AgGridColumn
						field="customer.first_name"
						headerName="First Name"
						filter={false}
						cellStyle={() => ({ textAlign: "left" })}
					></AgGridColumn>
					<AgGridColumn
						field="customer.last_name"
						headerName="Last Name"
						filter={false}
						cellStyle={() => ({ textAlign: "left" })}
					></AgGridColumn>
					<AgGridColumn
						field="phone"
						headerName="Phone"
						floatingFilterComponent="customFloatingFilter"
						filter="customFilter"
						cellStyle={() => ({ textAlign: "left" })}
					></AgGridColumn>
					<AgGridColumn
						field="customer_scan_id"
						headerName="Scan Id"
						floatingFilterComponent="customFloatingFilter"
						filter="customFilter"
						cellStyle={() => ({ textAlign: "left" })}
					></AgGridColumn>
					<AgGridColumn
						field="device_id"
						headerName="Device Id"
						floatingFilterComponent="customFloatingFilter"
						filter="customFilter"
						cellStyle={() => ({ textAlign: "left" })}
					></AgGridColumn>
					<AgGridColumn
						field="date"
						headerName="Date Of Scan"
						cellRenderer="handleDateCellRenderer"
						filter={false}
						cellStyle={() => ({ textAlign: "left" })}
					></AgGridColumn>
					<AgGridColumn
						field="action"
						headerName="Report"
						cellRenderer="reportActionsCell"
						filter={false}
						minWidth={260}
					></AgGridColumn>
				</AgGridReact>
			</div>
			<div className="custom-pagination" id="custom-pagination">
				<CustomPagination pageCount={pageCount} currentPage={currentPage} handlePageChange={handlePageChange} />
			</div>
		</div>
	);
};

export default ScanReports;
